import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useQueryClient } from '@tanstack/react-query';
import { apiRequest, ApiError } from '../api/client';
import type { AcceptInvitationResponse } from '@dfx-transfer/shared-types';

export function AcceptInvitePage() {
  const { token } = useParams<{ token: string }>();
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<'idle' | 'accepting' | 'done' | 'error'>('idle');
  const [error, setError] = useState<string | null>(null);
  const [creds, setCreds] = useState<AcceptInvitationResponse['sftp_credentials'] | null>(null);

  useEffect(() => {
    if (isLoading) return;
    if (!token) {
      setError('missing invitation token');
      setStatus('error');
      return;
    }
    if (!isAuthenticated) {
      // appState gets dropped if Auth0 inserts an email-verification step
      // between signup and the final redirect. localStorage survives that round
      // trip; the App-level redirector below picks the token back up.
      window.localStorage.setItem('dfx_pending_invite', token);
      loginWithRedirect({
        appState: { returnTo: `/invite/${token}` },
        authorizationParams: { screen_hint: 'signup' },
      });
      return;
    }
    window.localStorage.removeItem('dfx_pending_invite');
    if (status !== 'idle') return;

    (async () => {
      setStatus('accepting');
      try {
        const accessToken = await getAccessTokenSilently();
        const resp = await apiRequest<AcceptInvitationResponse>(
          `/api/v1/invitations/${token}/accept`,
          { token: accessToken, method: 'POST' }
        );
        await queryClient.invalidateQueries({ queryKey: ['transfer-authz'] });
        setCreds(resp.sftp_credentials);
        setStatus('done');
      } catch (err) {
        setStatus('error');
        setError(err instanceof ApiError ? err.message : String(err));
      }
    })();
  }, [
    isLoading,
    isAuthenticated,
    token,
    loginWithRedirect,
    getAccessTokenSilently,
    queryClient,
    status,
  ]);

  if (status === 'error') {
    return (
      <div className="mx-auto max-w-md p-6 text-center">
        <h1 className="text-xl font-semibold">Couldn't accept invitation</h1>
        <p className="mt-2 text-[color:var(--color-text-secondary)]">{error}</p>
      </div>
    );
  }

  if (status === 'done' && creds) {
    return <CredentialsView creds={creds} onContinue={() => navigate('/', { replace: true })} />;
  }

  return <div className="p-6">Accepting invitation…</div>;
}

interface CredentialsProps {
  creds: AcceptInvitationResponse['sftp_credentials'];
  onContinue: () => void;
}

function CredentialsView({ creds, onContinue }: CredentialsProps) {
  const [acknowledged, setAcknowledged] = useState(false);
  const downloadHref = `data:application/octet-stream;charset=utf-8,${encodeURIComponent(
    creds.private_key_openssh
  )}`;
  return (
    <div className="mx-auto max-w-2xl px-6 py-10">
      <div className="font-mono text-xs uppercase tracking-widest text-[color:var(--color-text-muted)]">
        DFX Network · Transfer
      </div>
      <h1 className="mt-3 text-2xl font-semibold">Save your SFTP credentials</h1>
      <div className="mt-4 rounded border border-[color:var(--color-warning-muted)] bg-[color:var(--color-bg-card)] p-4 text-sm">
        <strong className="text-[color:var(--color-warning)]">This is shown once.</strong> The
        private key cannot be retrieved later. Download it now and store it somewhere safe (a
        password manager or your team's secret store).
      </div>

      <dl className="mt-6 grid grid-cols-[8rem_1fr] gap-y-2 text-sm">
        <dt className="text-[color:var(--color-text-secondary)]">Host</dt>
        <dd className="font-mono">{creds.host}</dd>
        <dt className="text-[color:var(--color-text-secondary)]">Port</dt>
        <dd className="font-mono">{creds.port}</dd>
        <dt className="text-[color:var(--color-text-secondary)]">Username</dt>
        <dd className="font-mono">{creds.username}</dd>
        <dt className="text-[color:var(--color-text-secondary)]">Auth</dt>
        <dd>SSH key only (no password)</dd>
      </dl>

      <div className="mt-6">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-[color:var(--color-text-secondary)]">
            Private key (OpenSSH)
          </h2>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => navigator.clipboard.writeText(creds.private_key_openssh)}
              className="rounded border border-[color:var(--color-border)] px-3 py-1 text-xs"
            >
              Copy
            </button>
            <a
              href={downloadHref}
              download={`${creds.username}.id_ed25519`}
              className="rounded bg-[color:var(--color-crimson)] px-3 py-1 text-xs font-medium text-white"
            >
              Download
            </a>
          </div>
        </div>
        <pre className="mt-2 overflow-auto rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-primary)] p-4 font-mono text-xs leading-relaxed">
          {creds.private_key_openssh}
        </pre>
      </div>

      <div className="mt-6 rounded border border-[color:var(--color-border)] bg-[color:var(--color-bg-card)] p-4 text-sm text-[color:var(--color-text-secondary)]">
        <p className="text-[color:var(--color-text-primary)]">Quick connect</p>
        <pre className="mt-2 overflow-auto font-mono text-xs">
{`chmod 600 ${creds.username}.id_ed25519
sftp -i ${creds.username}.id_ed25519 -P ${creds.port} ${creds.username}@${creds.host}`}
        </pre>
        <p className="mt-2 text-xs">
          The <code className="font-mono">chmod 600</code> step is required — OpenSSH refuses
          private key files that other users can read.
        </p>
      </div>

      <label className="mt-6 flex items-center gap-2 text-sm">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
        />
        I've saved the private key.
      </label>
      <button
        type="button"
        disabled={!acknowledged}
        onClick={onContinue}
        className="mt-4 rounded bg-[color:var(--color-crimson)] px-5 py-2 font-medium text-white disabled:opacity-50"
      >
        Continue
      </button>
    </div>
  );
}
